import { useState, useEffect, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft, ShoppingBag, MessageCircle, Star, Leaf, Plus, Eye, Users,
  Shield, Zap, Brain, Heart, Bone, Sparkles, Moon, Sun,
  Droplets, RefreshCw, Flame, Recycle, Move, Dumbbell,
  Smile, Activity, ShieldCheck, Microscope, HeartPulse
} from "lucide-react";
import Navbar from "../components/landing/Navbar";
import Footer from "../components/landing/Footer";
import { useCart } from "../contexts/CartContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICON_MAP = {
  "shield": Shield, "zap": Zap, "brain": Brain, "heart": Heart,
  "bone": Bone, "sparkles": Sparkles, "moon": Moon, "sun": Sun,
  "droplets": Droplets, "refresh-cw": RefreshCw, "flame": Flame,
  "recycle": Recycle, "move": Move, "dumbbell": Dumbbell,
  "smile": Smile, "activity": Activity, "shield-check": ShieldCheck,
  "microscope": Microscope, "heart-pulse": HeartPulse, "leaf": Leaf,
};

function formatPrice(price) {
  return new Intl.NumberFormat("fr-FR").format(price) + " FCFA";
}

export default function ProductDetailPage() {
  const { id } = useParams();
  const { addItem } = useCart();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [allProducts, setAllProducts] = useState([]);
  const [imgError, setImgError] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [reviewForm, setReviewForm] = useState({ name: "", city: "", rating: 5, text: "" });
  const [submittingReview, setSubmittingReview] = useState(false);

  useEffect(() => {
    async function fetchData() {
      try {
        const [prodRes, allRes, reviewsRes] = await Promise.all([
          axios.get(`${API}/products/${id}`),
          axios.get(`${API}/products`),
          axios.get(`${API}/products/${id}/reviews`),
        ]);
        setProduct(prodRes.data);
        setAllProducts(allRes.data.filter((p) => p.id !== id).slice(0, 3));
        setReviews(reviewsRes.data);
      } catch (err) {
        console.error("Error fetching product:", err);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
    window.scrollTo(0, 0);
  }, [id]);

  async function handleSubmitReview(e) {
    e.preventDefault();
    setSubmittingReview(true);
    try {
      const res = await axios.post(`${API}/products/${id}/reviews`, reviewForm);
      setReviews([res.data, ...reviews]);
      setShowReviewForm(false);
      setReviewForm({ name: "", city: "", rating: 5, text: "" });
    } catch (err) {
      console.error("Error submitting review:", err);
    } finally {
      setSubmittingReview(false);
    }
  }

  const avgRating = reviews.length > 0
    ? (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1)
    : 0;

  // Social proof: pseudo-stable "people viewing" + "purchases this week"
  // Derived deterministically from product ID to avoid hydration jumps
  const socialProof = useMemo(() => {
    if (!id) return { viewing: 0, purchases: 0 };
    let seed = 0;
    for (let i = 0; i < id.length; i++) seed = (seed * 31 + id.charCodeAt(i)) & 0xffffffff;
    const viewing = 4 + (Math.abs(seed) % 18); // 4-21 people
    const purchases = 12 + (Math.abs(seed >> 8) % 47); // 12-58 purchases
    return { viewing, purchases };
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F0E8]">
        <Navbar />
        <div className="max-w-7xl mx-auto px-4 py-24 text-center">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-stone-200 rounded w-1/3 mx-auto" />
            <div className="h-64 bg-stone-200 rounded-2xl max-w-md mx-auto" />
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-[#F5F0E8]">
        <Navbar />
        <div className="max-w-7xl mx-auto px-4 py-24 text-center">
          <h1 className="text-2xl font-bold text-stone-900 mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
            Produit non trouvé
          </h1>
          <Link to="/" className="text-[#6C3FC5] hover:underline">Retour à l'accueil</Link>
        </div>
        <Footer />
      </div>
    );
  }

  const whatsappUrl = `https://wa.me/2250716421345?text=${encodeURIComponent(
    `Bonjour, je souhaite commander ${product.name} (${product.short_name}) - ${formatPrice(product.price)}`
  )}`;

  const whatsappExpert = `https://wa.me/2250716421345?text=${encodeURIComponent(
    `Bonjour, j'aimerais en savoir plus sur le produit ${product.name}`
  )}`;

  return (
    <div className="min-h-screen bg-[#F5F0E8]">
      <Navbar />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-16">
        {/* Breadcrumb */}
        <Link
          to="/#produits"
          data-testid="product-detail-back"
          className="inline-flex items-center gap-2 text-stone-500 hover:text-[#6C3FC5] transition-colors text-sm mb-8"
        >
          <ArrowLeft size={16} />
          Retour aux produits
        </Link>

        {/* Product Hero */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-16 mb-16">
          {/* Image */}
          <div className="relative">
            <div className="bg-white rounded-3xl overflow-hidden shadow-[0_20px_50px_rgb(108,63,197,0.1)] aspect-square">
              {!imgError ? (
                <img
                  src={product.image_url}
                  alt={product.name}
                  data-testid="product-detail-image"
                  className="w-full h-full object-cover"
                  onError={() => setImgError(true)}
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#6C3FC5]/10 to-[#6C3FC5]/5">
                  <span className="text-[#6C3FC5] font-bold text-5xl" style={{ fontFamily: "'Playfair Display', serif" }}>
                    {product.short_name}
                  </span>
                </div>
              )}
            </div>
            <div className="absolute -bottom-3 -right-3 bg-[#6C3FC5] text-white px-5 py-2 rounded-full text-sm font-medium shadow-lg">
              {product.short_name}
            </div>
            {product.discount_percentage > 0 && (
              <div className="absolute top-4 left-4 bg-red-500 text-white px-4 py-1.5 rounded-full text-sm font-bold shadow-lg">
                -{product.discount_percentage}%
              </div>
            )}
          </div>

          {/* Info */}
          <div className="flex flex-col justify-center">
            <span className="inline-block bg-[#6C3FC5]/10 text-[#6C3FC5] px-3 py-1 rounded-full text-xs font-medium mb-4 w-fit">
              {product.category}
            </span>

            <h1
              data-testid="product-detail-name"
              className="text-3xl sm:text-4xl lg:text-5xl font-bold text-stone-900 mb-4"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              {product.name}
            </h1>

            <p className="text-stone-600 text-base md:text-lg leading-relaxed mb-6">
              {product.description}
            </p>

            <div data-testid="product-detail-price" className="mb-8 flex items-end gap-3">
              <span className="text-3xl font-bold text-[#6C3FC5]">
                {formatPrice(product.price)}
              </span>
              {product.discount_percentage > 0 && product.original_price > 0 && (
                <>
                  <span className="text-xl text-stone-400 line-through">
                    {formatPrice(product.original_price)}
                  </span>
                  <span className="bg-red-500 text-white px-3 py-1 rounded-full text-xs font-bold">
                    -{product.discount_percentage}%
                  </span>
                </>
              )}
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => addItem(product)}
                disabled={!product.in_stock}
                data-testid="product-detail-add-cart"
                className="inline-flex items-center justify-center gap-2 bg-[#6C3FC5] hover:bg-[#582FB0] disabled:bg-stone-300 disabled:cursor-not-allowed text-white px-8 py-3.5 rounded-full text-base font-medium transition-all duration-200 hover:scale-105 shadow-lg shadow-[#6C3FC5]/25"
              >
                <Plus size={18} />
                Ajouter au panier
              </button>
              <a
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="product-detail-commander"
                className="inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#128C7E] text-white px-8 py-3.5 rounded-full text-base font-medium transition-all duration-200 hover:scale-105"
              >
                <ShoppingBag size={18} />
                Commander direct
              </a>
              <a
                href={whatsappExpert}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="product-detail-expert"
                className="inline-flex items-center justify-center gap-2 border-2 border-[#6C3FC5] text-[#6C3FC5] hover:bg-[#6C3FC5] hover:text-white px-6 py-3.5 rounded-full text-base font-medium transition-all duration-200"
              >
                <MessageCircle size={18} />
                Expert
              </a>
            </div>

            {/* Social proof */}
            <div data-testid="social-proof" className="mt-6 flex flex-wrap items-center gap-4 text-xs text-stone-500">
              <span className="inline-flex items-center gap-1.5">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#10B981] opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-[#10B981]"></span>
                </span>
                <Eye size={13} className="text-stone-400" />
                <strong className="text-stone-700">{socialProof.viewing}</strong> personnes consultent ce produit
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Users size={13} className="text-[#6C3FC5]" />
                <strong className="text-stone-700">{socialProof.purchases}</strong> achats cette semaine
              </span>
            </div>
          </div>
        </div>

        {/* Long Description */}
        {product.long_description && (
          <div data-testid="product-detail-long-description" className="bg-white rounded-3xl p-8 md:p-12 mb-12 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
            <h2
              className="text-2xl md:text-3xl font-semibold text-stone-900 mb-6"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Description détaillée
            </h2>
            <div className="text-stone-600 text-base md:text-lg leading-relaxed space-y-4">
              {product.long_description.split("\n").map((para, i) => (
                <p key={i}>{para}</p>
              ))}
            </div>
          </div>
        )}

        {/* Benefits */}
        {product.benefits && product.benefits.length > 0 && (
          <div data-testid="product-detail-benefits" className="mb-12">
            <h2
              className="text-2xl md:text-3xl font-semibold text-stone-900 mb-8 text-center"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Bienfaits principaux
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {product.benefits.map((benefit, i) => {
                const IconComp = ICON_MAP[benefit.icon] || Shield;
                return (
                  <div
                    key={i}
                    data-testid={`product-benefit-${i}`}
                    className="bg-white rounded-2xl p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-lg transition-all duration-300 hover:-translate-y-0.5"
                  >
                    <div className="w-11 h-11 rounded-xl bg-[#6C3FC5]/10 flex items-center justify-center mb-4">
                      <IconComp size={20} className="text-[#6C3FC5]" />
                    </div>
                    <h3 className="font-semibold text-stone-900 mb-1.5 text-base">
                      {benefit.title}
                    </h3>
                    <p className="text-stone-500 text-sm leading-relaxed">
                      {benefit.description}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Ingredients */}
        {product.ingredients && (
          <div data-testid="product-detail-ingredients" className="bg-white rounded-3xl p-8 md:p-12 mb-12 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
            <h2
              className="text-2xl md:text-3xl font-semibold text-stone-900 mb-6"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              <Leaf size={24} className="inline mr-2 text-[#10B981]" />
              Ingrédients clés
            </h2>
            <div className="flex flex-wrap gap-3">
              {product.ingredients.split("•").filter(Boolean).map((ingredient, i) => (
                <span
                  key={i}
                  className="inline-flex items-center bg-[#F5F0E8] text-stone-700 px-4 py-2 rounded-full text-sm font-medium"
                >
                  {ingredient.trim()}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Reviews Section */}
        <div data-testid="product-reviews-section" className="mb-12">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2
                className="text-2xl md:text-3xl font-semibold text-stone-900 mb-2"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Avis clients
              </h2>
              {reviews.length > 0 && (
                <div className="flex items-center gap-3">
                  <div className="flex gap-0.5">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} size={16} className={i < Math.round(avgRating) ? "text-[#F59E0B] fill-[#F59E0B]" : "text-stone-300"} />
                    ))}
                  </div>
                  <span className="text-stone-600 text-sm font-medium">{avgRating}/5 ({reviews.length} avis)</span>
                </div>
              )}
            </div>
            <button
              data-testid="write-review-btn"
              onClick={() => setShowReviewForm(!showReviewForm)}
              className="inline-flex items-center gap-2 bg-[#6C3FC5] hover:bg-[#582FB0] text-white px-5 py-2.5 rounded-full text-sm font-medium transition-all"
            >
              <Star size={15} />
              Donner un avis
            </button>
          </div>

          {/* Review Form */}
          {showReviewForm && (
            <div className="bg-white rounded-2xl p-6 md:p-8 mb-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
              <h3 className="font-semibold text-stone-900 mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                Votre avis sur {product.name}
              </h3>
              <form onSubmit={handleSubmitReview} className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-stone-600 mb-1">Nom</label>
                    <input
                      data-testid="review-name-input"
                      type="text" required value={reviewForm.name}
                      onChange={(e) => setReviewForm({ ...reviewForm, name: e.target.value })}
                      className="w-full px-3 py-2.5 rounded-xl border border-stone-200 focus:border-[#6C3FC5] focus:ring-2 focus:ring-[#6C3FC5]/20 outline-none text-sm"
                      placeholder="Votre prénom"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-stone-600 mb-1">Ville</label>
                    <input
                      data-testid="review-city-input"
                      type="text" value={reviewForm.city}
                      onChange={(e) => setReviewForm({ ...reviewForm, city: e.target.value })}
                      className="w-full px-3 py-2.5 rounded-xl border border-stone-200 focus:border-[#6C3FC5] focus:ring-2 focus:ring-[#6C3FC5]/20 outline-none text-sm"
                      placeholder="Votre ville"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm text-stone-600 mb-2">Note</label>
                  <div className="flex gap-1">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star} type="button"
                        data-testid={`review-star-${star}`}
                        onClick={() => setReviewForm({ ...reviewForm, rating: star })}
                        className="p-0.5"
                      >
                        <Star size={28} className={star <= reviewForm.rating ? "text-[#F59E0B] fill-[#F59E0B]" : "text-stone-300"} />
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-sm text-stone-600 mb-1">Votre avis</label>
                  <textarea
                    data-testid="review-text-input"
                    required value={reviewForm.text}
                    onChange={(e) => setReviewForm({ ...reviewForm, text: e.target.value })}
                    className="w-full px-3 py-2.5 rounded-xl border border-stone-200 focus:border-[#6C3FC5] focus:ring-2 focus:ring-[#6C3FC5]/20 outline-none text-sm"
                    rows={3}
                    placeholder="Partagez votre expérience avec ce produit..."
                  />
                </div>
                <div className="flex gap-3 justify-end">
                  <button type="button" onClick={() => setShowReviewForm(false)}
                    className="px-4 py-2 text-sm text-stone-600 hover:text-stone-900">
                    Annuler
                  </button>
                  <button
                    type="submit"
                    data-testid="review-submit-btn"
                    disabled={submittingReview}
                    className="bg-[#6C3FC5] hover:bg-[#582FB0] text-white px-6 py-2.5 rounded-full text-sm font-medium transition-all disabled:opacity-50"
                  >
                    {submittingReview ? "Envoi..." : "Publier l'avis"}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Reviews List */}
          {reviews.length > 0 ? (
            <div className="space-y-4">
              {reviews.map((review, i) => (
                <div key={review.id} data-testid={`product-review-${i}`}
                  className="bg-white rounded-2xl p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-[#6C3FC5] flex items-center justify-center text-white font-medium text-sm">
                        {review.name.charAt(0)}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-stone-900 text-sm">{review.name}</p>
                          {review.verified && (
                            <span className="text-[10px] bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">Achat vérifié</span>
                          )}
                        </div>
                        {review.city && <p className="text-stone-500 text-xs">{review.city}</p>}
                      </div>
                    </div>
                    <div className="flex gap-0.5">
                      {[...Array(5)].map((_, j) => (
                        <Star key={j} size={14} className={j < review.rating ? "text-[#F59E0B] fill-[#F59E0B]" : "text-stone-300"} />
                      ))}
                    </div>
                  </div>
                  <p className="text-stone-600 text-sm leading-relaxed">{review.text}</p>
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-white rounded-2xl p-10 text-center shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
              <Star size={40} className="text-stone-300 mx-auto mb-3" />
              <p className="text-stone-500 text-sm">Aucun avis pour ce produit. Soyez le premier !</p>
            </div>
          )}
        </div>

        {/* Related Products */}
        {allProducts.length > 0 && (
          <div data-testid="related-products" className="mt-16">
            <h2
              className="text-2xl md:text-3xl font-semibold text-stone-900 mb-8 text-center"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Vous aimerez aussi
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {allProducts.map((p) => (
                <Link
                  key={p.id}
                  to={`/produit/${p.id}`}
                  data-testid={`related-product-${p.id}`}
                  className="group bg-white rounded-2xl overflow-hidden shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
                >
                  <div className="aspect-[4/3] bg-stone-50 overflow-hidden">
                    <img
                      src={p.image_url}
                      alt={p.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-5">
                    <h3 className="font-bold text-stone-900 text-lg mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>
                      {p.name}
                    </h3>
                    <p className="text-stone-500 text-sm mb-3 line-clamp-2">{p.description}</p>
                    <p className="text-[#6C3FC5] font-semibold">{formatPrice(p.price)}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
}
